import type { RenderContext, VideoFrame } from '../../types';
import { BaseEffect } from '../base';
import { effectSeed, withOverlay } from './composite';
import { hash01 } from '../../utils/deterministic';

const DEFAULTS = { intensity: 0.75, colorMode: 0 };

const PALETTES = [
  // warm film
  [[255, 140, 70], [255, 190, 110], [255, 95, 60]],
  // cool daylight
  [[90, 160, 255], [140, 210, 255], [70, 120, 220]],
  // rainbow / neon
  [[255, 90, 200], [120, 220, 255], [255, 200, 80]],
];

export class LightLeaksEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'light_leaks', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    return this.renderCpu(frame, params, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const seed = effectSeed(context, this.id);
    const t = context.frameIndex * 0.028;
    const palette = PALETTES[Math.min(2, Math.max(0, Math.round(params.colorMode)))] || PALETTES[0];
    return withOverlay(frame, (ctx, w, h) => {
      const alpha = params.intensity * 0.42;

      for (let i = 0; i < 4; i += 1) {
        const [rv, gv, bv] = palette[i % palette.length];
        const ox = (hash01(i, 0, seed) * 0.75 + Math.sin(t * (0.7 + i * 0.2) + i) * 0.12) * w;
        const oy = (hash01(i, 1, seed) * 0.7 + Math.cos(t * 0.55 + i) * 0.1) * h;
        const r = Math.max(w, h) * (0.28 + hash01(i, 2, seed) * 0.35);
        const g = ctx.createRadialGradient(ox, oy, 0, ox, oy, r);
        g.addColorStop(0, `rgba(${rv},${gv},${bv},${alpha * (0.85 - i * 0.12)})`);
        g.addColorStop(0.45, `rgba(${rv},${gv},${bv},${alpha * 0.28})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }

      // Soft edge wash / film burn along frame borders
      for (let i = 0; i < 2; i += 1) {
        const [rv, gv, bv] = palette[i];
        const fromLeft = hash01(i, 3, seed) > 0.5;
        const x0 = fromLeft ? 0 : w;
        const wash = ctx.createLinearGradient(x0, 0, fromLeft ? w * 0.45 : w * 0.55, 0);
        wash.addColorStop(0, `rgba(${rv},${gv},${bv},${alpha * 0.55})`);
        wash.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = wash;
        ctx.fillRect(0, 0, w, h);
      }

      // Thin anamorphic-ish leak streak
      const sy = (0.2 + hash01(7, 0, seed) * 0.6) * h;
      const streak = ctx.createLinearGradient(0, sy, w, sy);
      const [r0, g0, b0] = palette[0];
      streak.addColorStop(0, 'rgba(255,255,255,0)');
      streak.addColorStop(0.35, `rgba(${r0},${g0},${b0},${alpha * 0.35})`);
      streak.addColorStop(0.5, `rgba(255,245,230,${alpha * 0.55})`);
      streak.addColorStop(0.65, `rgba(${r0},${g0},${b0},${alpha * 0.3})`);
      streak.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = streak;
      ctx.fillRect(0, sy - h * 0.02, w, h * 0.04);
    }, 'lighter');
  }
}
